import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../../services/api';
import { FiPlus, FiEdit, FiTrash2, FiX } from 'react-icons/fi';
import './Management.css';

const BannersManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    file_path: '',
    file_type: 'image',
    order: 0,
    is_active: true,
    duration: 5000,
  });

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      const response = await adminAPI.getAllBanners();
      setBanners(response.data);
    } catch (error) {
      console.error('Error loading banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBanner) {
        await adminAPI.updateBanner(editingBanner.id, formData);
      } else {
        await adminAPI.createBanner(formData);
      }
      loadBanners();
      resetForm();
    } catch (error) {
      alert('Error al guardar el banner');
      console.error(error);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || '',
      file_path: banner.file_path || '',
      file_type: banner.file_type || 'image',
      order: banner.order || 0,
      is_active: banner.is_active,
      duration: banner.duration || 5000,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este banner?')) {
      try {
        await adminAPI.deleteBanner(id);
        loadBanners();
      } catch (error) {
        alert('Error al eliminar el banner');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      file_path: '',
      file_type: 'image',
      order: 0,
      is_active: true,
      duration: 5000,
    });
    setEditingBanner(null);
    setShowForm(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tamaño (500MB)
    if (file.size > 500 * 1024 * 1024) {
      alert('El archivo es demasiado pesado. El límite es de 500MB.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await adminAPI.uploadFile(file, (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
      });
      setFormData({ ...formData, file_path: response.data.file_path });
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error.response && error.response.status === 413) {
        alert('Error 413: El archivo es demasiado grande para el servidor. Debes aumentar el límite "client_max_body_size" en la configuración de Nginx.');
      } else {
        alert('Error al subir el archivo');
      }
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="loading">Cargando...</div>;

  return (
    <div className="management">
      <div className="management-header">
        <h1>Gestión de Banners</h1>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          <FiPlus /> Nuevo Banner
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingBanner ? 'Editar' : 'Nuevo'} Banner</h2>
              <button className="close-btn" onClick={resetForm}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="form">
              <div className="form-group">
                <label>Título</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Archivo</label>
                <input
                  type="file"
                  onChange={handleFileUpload}
                  accept={formData.file_type === 'video' ? 'video/*' : 'image/*'}
                />
                {uploading && (
                  <div style={{ marginTop: '10px' }}>
                    <div style={{ width: '100%', backgroundColor: '#eee', borderRadius: '4px', height: '10px' }}>
                      <div
                        style={{
                          width: `${uploadProgress}%`,
                          backgroundColor: '#2563eb',
                          height: '100%',
                          borderRadius: '4px',
                          transition: 'width 0.3s ease'
                        }}
                      />
                    </div>
                    <p style={{ fontSize: '0.8em', color: '#666', textAlign: 'center', marginTop: '5px' }}>Subiendo: {uploadProgress}%</p>
                  </div>
                )}
                <input
                  type="text"
                  value={formData.file_path}
                  readOnly
                  placeholder="La ruta se generará automáticamente"
                  required
                />
                {formData.file_path && (
                  <div className="preview-container">
                    {formData.file_type === 'video' ? (
                      <video src={`/api/files/${formData.file_path}`} controls className="form-preview" />
                    ) : (
                      <img src={`/api/files/${formData.file_path}`} alt="Vista previa" className="form-preview" />
                    )}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Tipo de archivo</label>
                <select
                  value={formData.file_type}
                  onChange={(e) => setFormData({ ...formData, file_type: e.target.value })}
                >
                  <option value="image">Imagen</option>
                  <option value="video">Video</option>
                </select>
              </div>
              <div className="form-group">
                <label>Duración (ms)</label>
                <input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                />
                <small style={{color: '#666'}}>5000 ms = 5 segundos</small>
              </div>
              <div className="form-group">
                <label>Orden</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Activo
                </label>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Título</th>
              <th>Vista Previa</th>
              <th>Orden</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {banners.map((banner) => (
              <tr key={banner.id}>
                <td>{banner.id}</td>
                <td>{banner.title || '-'}</td>
                <td>
                  {banner.file_type === 'video' ? (
                    <video
                      src={`/api/files/${banner.file_path}`}
                      className="table-preview"
                      muted
                      onMouseOver={(e) => {
                        const playPromise = e.target.play();
                        if (playPromise !== undefined) {
                          playPromise.catch(() => {});
                        }
                      }}
                      onMouseOut={(e) => e.target.pause()}
                    />
                  ) : (
                    <img
                      src={`/api/files/${banner.file_path}`}
                      alt={banner.title}
                      className="table-preview"
                    />
                  )}
                </td>
                <td>{banner.order}</td>
                <td>{banner.is_active ? 'Activo' : 'Inactivo'}</td>
                <td>
                  <button
                    className="btn-icon"
                    onClick={() => handleEdit(banner)}
                    title="Editar"
                  >
                    <FiEdit />
                  </button>
                  <button
                    className="btn-icon btn-danger"
                    onClick={() => handleDelete(banner.id)}
                    title="Eliminar"
                  >
                    <FiTrash2 />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BannersManagement;
